import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { EventPagination } from '../components/events/EventPagination';
import DataTableShell from '../components/common/DataTableShell';
import { checkpointsService } from '../services/checkpointsService';
import { Copy, Check } from 'lucide-react';

// Data shape for integrity checkpoints
interface CheckpointRow {
    checkpointId: number;
    aptosTxHash: string;
    aptosBlockNumber: number;
    besuStartBlock: number;
    besuEndBlock: number;
}

// Data will be fetched from API

const IntegrityChecker: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [rows, setRows] = useState<CheckpointRow[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [copiedHash, setCopiedHash] = useState<string | null>(null);

    useEffect(() => {
        const run = async () => {
            try {
                setLoading(true);
                const res = await checkpointsService.getCheckpoints(currentPage, itemsPerPage);
                const mapped: CheckpointRow[] = res.checkpoints.map((c) => ({
                    checkpointId: c.checkpointId,
                    aptosTxHash: c.aptosTxHash,
                    aptosBlockNumber: c.aptosBlockNumber,
                    besuStartBlock: c.startBlock,
                    besuEndBlock: c.endBlock,
                }));
                setRows(mapped);
                setTotalRows(res.totalCount);
                setTotalPages(res.totalPages);
            } catch (err) {
                console.error('Error fetching checkpoints', err);
                setRows([]);
                setTotalRows(0);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [currentPage, itemsPerPage]);

    const pagedRows = rows; // server-side pagination

    const goToPage = (p: number) => {
        const next = Math.min(Math.max(1, p), totalPages);
        setCurrentPage(next);
    };

    const onItemsPerPageChange = (n: number) => {
        setItemsPerPage(n);
        setCurrentPage(1);
    };

    return (
        <div className="space-y-6">
            <Card className="overflow-hidden">
                <DataTableShell headers={[{ label: 'Checkpoint ID' }, { label: 'Aptos Tx Hash' }, { label: 'Aptos Block Number' }, { label: 'Besu Start Block' }, { label: 'Besu End Block' }, { label: 'Action', align: 'center', className: 'w-24' }]}>
                    {loading ? (
                        <tr>
                            <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                                Loading...
                            </td>
                        </tr>
                    ) : pagedRows.length === 0 ? (
                        <tr>
                            <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                                No checkpoints found.
                            </td>
                        </tr>
                    ) : (
                        pagedRows.map((row) => (
                            <tr key={row.checkpointId} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900">{row.checkpointId}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <a href={`https://explorer.aptoslabs.com/txn/${row.aptosTxHash}?network=mainnet`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-mono">
                                            {`${row.aptosTxHash.slice(0, 12)}...${row.aptosTxHash.slice(-8)}`}
                                        </a>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(row.aptosTxHash);
                                                setCopiedHash(row.aptosTxHash);
                                                setTimeout(() => setCopiedHash(null), 2000);
                                            }}
                                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                                            title="Copy Aptos transaction hash"
                                        >
                                            {copiedHash === row.aptosTxHash ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
                                        </button>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <a href={`https://explorer.aptoslabs.com/txn/${row.aptosBlockNumber}?network=mainnet`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                        {row.aptosBlockNumber.toLocaleString()}
                                    </a>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900">{row.besuStartBlock.toLocaleString()}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900">{row.besuEndBlock.toLocaleString()}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center justify-center">
                                        <Link
                                            to={`/integrity-checker/${row.checkpointId}/txns`}
                                            state={{
                                                checkpointId: row.checkpointId,
                                                startBlock: row.besuStartBlock,
                                                endBlock: row.besuEndBlock,
                                            }}
                                            className="text-blue-600 hover:underline"
                                        >
                                            View Txns
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </DataTableShell>

                <EventPagination currentPage={currentPage} totalPages={totalPages} totalEvents={totalRows} itemsPerPage={itemsPerPage} onPageChange={goToPage} onItemsPerPageChange={onItemsPerPageChange} />
            </Card>
        </div>
    );
};

export default IntegrityChecker;
